'use client'

import { useState, useEffect } from 'react'
import { X, Sparkles, ArrowRight } from 'lucide-react'
import { Button } from './ui/button'

interface NewsletterPopupProps {
  onClose: () => void
  isVisible: boolean
}

export function NewsletterPopup({ onClose, isVisible }: NewsletterPopupProps) {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || isSubmitting) return

    setIsSubmitting(true)
    try {
      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      if (response.ok) {
        setIsSuccess(true)
        // Guardar en localStorage que se suscribió
        localStorage.setItem('newsletterSubscribed', 'true')
        localStorage.setItem('newsletterSubscribedAt', new Date().toISOString())
        // Cerrar después de 2 segundos
        setTimeout(() => {
          onClose()
        }, 2000)
      } else {
        throw new Error('Error al suscribirse')
      }
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isVisible) return null

  return (
    <div className='fixed bottom-6 right-6 z-50 animate-slide-in-right'>
      <div className='bg-white rounded-2xl shadow-2xl p-6 w-80 max-w-[90vw] border border-gray-100'>
        {/* Header */}
        <div className='flex items-center justify-between mb-4'>
          <div className='flex items-center gap-3'>
            <div className='w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center'>
              <Sparkles className='w-5 h-5 text-white' />
            </div>
            <h3 className='font-bold text-gray-800 text-lg'>
              ¡No te pierdas nada!
            </h3>
          </div>
          <button
            onClick={onClose}
            className='text-gray-500 hover:text-gray-700 transition-colors'
          >
            <X className='w-5 h-5' />
          </button>
        </div>

        {/* Body */}
        <p className='text-gray-600 mb-4 text-sm leading-relaxed'>
          Recibe las últimas novedades y ofertas exclusivas directamente en tu
          email.
        </p>

        {/* Form */}
        {!isSuccess ? (
          <form onSubmit={handleSubmit} className='space-y-3'>
            <div className='flex gap-2'>
              <input
                type='email'
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder='tu@email.com'
                className='flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm'
                required
              />
              <Button
                type='submit'
                disabled={isSubmitting}
                className='bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50'
              >
                {isSubmitting ? (
                  <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin' />
                ) : (
                  <ArrowRight className='w-4 h-4' />
                )}
              </Button>
            </div>
          </form>
        ) : (
          <div className='text-center py-2'>
            <div className='text-green-600 font-medium text-sm'>
              ¡Gracias por suscribirte! 🎉
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
